import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { capacitorPlatform, isCapacitorNative } from '../utils/isCapacitorNative';

let initializedClientId: string | null = null;

export type NativeGoogleSignInFailureKind =
  | 'cancelled'
  | 'notReady'
  | 'developerError'
  | 'noIdToken'
  | 'unknown';

function pluginErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const code = (error as { code?: unknown }).code;
  if (code == null || code === '') {
    return null;
  }
  return String(code);
}

export function nativeGoogleSignInErrorBlob(error: unknown): string {
  if (error instanceof Error) {
    const extra = error as Error & { code?: unknown };
    return [error.message, extra.code != null ? String(extra.code) : '']
      .filter((part) => part.trim().length > 0)
      .join(' ');
  }
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
  return String(error ?? '');
}

export function classifyNativeGoogleSignInError(error: unknown): NativeGoogleSignInFailureKind {
  if (isNativeGoogleSignInCancelled(error)) {
    return 'cancelled';
  }
  const code = pluginErrorCode(error);
  const blob = nativeGoogleSignInErrorBlob(error);
  if (code === '10' || /DEVELOPER_ERROR|ApiException:\s*10\b/i.test(blob)) {
    return 'developerError';
  }
  if (/not ready|initialize\(\) first/i.test(blob)) {
    return 'notReady';
  }
  if (/did not return an ID token/i.test(blob)) {
    return 'noIdToken';
  }
  return 'unknown';
}

async function ensureNativeGoogleAuthInitialized(webClientId: string): Promise<void> {
  const clientId = webClientId.trim();
  if (!clientId) {
    throw new Error('Google Sign-In is not configured');
  }
  if (initializedClientId === clientId) {
    return;
  }
  const initResult = GoogleAuth.initialize({
    clientId,
    scopes: ['profile', 'email'],
  }) as void | Promise<void>;
  if (initResult && typeof (initResult as Promise<void>).then === 'function') {
    await initResult;
  }
  initializedClientId = clientId;
}

export function isNativeGoogleSignInAvailable(): boolean {
  return isCapacitorNative();
}

function isNativeIos(): boolean {
  return isCapacitorNative() && capacitorPlatform() === 'ios';
}

/**
 * The iOS Google SDK needs an iOS OAuth client (and its reversed-ID URL scheme in Info.plist);
 * given the web client it crashes the app on sign-in. Android uses the web client.
 */
function nativeGoogleClientId(webClientId: string): string {
  if (isNativeIos()) {
    return process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID?.trim() ?? '';
  }
  return webClientId;
}

/** False in the iOS app until REACT_APP_GOOGLE_IOS_CLIENT_ID is set; Google web sign-in is blocked in WKWebView. */
export function isGoogleSignInSupportedOnThisDevice(): boolean {
  return !isNativeIos() || Boolean(process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID?.trim());
}

export function isNativeGoogleSignInCancelled(error: unknown): boolean {
  const code = pluginErrorCode(error);
  if (code === '12501') {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : nativeGoogleSignInErrorBlob(error);
  return /cancel|dismiss|closed|user.*abort/i.test(message);
}

async function signOutGoogleAuthBestEffort(): Promise<void> {
  try {
    await GoogleAuth.signOut();
  } catch {
    /* no prior Google session */
  }
}

/**
 * Drop the cached Google account so the next Sign in with Google shows the picker.
 * Safe to call when the plugin is missing or the user never used Google.
 */
export async function signOutNativeGoogleAuth(): Promise<void> {
  if (typeof window !== 'undefined') {
    try {
      window.google?.accounts?.id?.disableAutoSelect?.();
    } catch {
      /* GIS not loaded */
    }
  }
  if (!isCapacitorNative()) {
    initializedClientId = null;
    return;
  }
  const clientId = nativeGoogleClientId(process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID?.trim() ?? '');
  try {
    if (!clientId) {
      return;
    }
    await ensureNativeGoogleAuthInitialized(clientId);
    await signOutGoogleAuthBestEffort();
  } catch {
    /* ignore */
  } finally {
    initializedClientId = null;
  }
}

/** Native Google Sign-In (Android/iOS). Returns a Google ID token for the existing backend flow. */
export async function requestNativeGoogleIdToken(webClientId: string): Promise<string> {
  if (!isCapacitorNative()) {
    throw new Error('Native Google Sign-In is only available in the mobile app');
  }
  await ensureNativeGoogleAuthInitialized(nativeGoogleClientId(webClientId));
  // Otherwise Android reuses the last Google account with no picker after app logout.
  await signOutGoogleAuthBestEffort();
  const result = await GoogleAuth.signIn();
  const idToken = result.authentication?.idToken?.trim();
  if (!idToken) {
    throw new Error('Google Sign-In did not return an ID token');
  }
  return idToken;
}
